/**
 * City-level location landing pages for local SEO.
 *
 * Each city gets its own page in its NATIVE language only — no auto-
 * translation to 19 locales. Local SEO is inherently single-language;
 * a Japanese-speaking visitor searching for London is rare-to-nonexistent.
 *
 * Slug pattern:
 *   /cctv-design-{city}        — EN-language cities (UK + US)
 *   /pl/projekt-cctv-{miasto}  — Polish cities under /pl prefix
 *
 * Per-city data is hand-curated from public sources (statistical offices,
 * local business directories, EU/state regulatory pages 2025-2026). Values
 * reflect the "shape" of the local market — they're starting points, not
 * one-true-source. Future sprints can deepen each with local installer
 * directories, distributor presence, regional pricing.
 *
 * Each city page renders fresh content because it combines the city
 * narrative + local industry mix + relevant compliance frameworks +
 * cross-links to sprint 1-3 outputs (calcs, brand pairs, v×d landings).
 */

export type LocaleKey = 'en' | 'pl' | 'de' | 'it' | 'es' | 'cs' | 'pt-br' | 'nl' | 'hu' | 'lt';

export interface CityFacts {
  /** URL slug — kebab-case, ASCII only */
  slug: string;
  /** Display name in the local language */
  name: string;
  /** Country name (display) */
  country: string;
  /** Locale this city renders in */
  locale: LocaleKey;
  /** Approximate metropolitan population */
  populationK: number;
  /** Top 3-4 industries / common project verticals in this city */
  topIndustries: string[];
  /** Climate factor affecting camera selection */
  climateNote: string;
  /** Applicable regulatory frameworks */
  regulations: string[];
  /** Brand presence — top brands with strong local distributor network */
  topLocalBrands: string[];
  /** One-paragraph local context narrative */
  intro: string;
  /**
   * Grammatical inflections for languages that decline city names (PL, CS).
   * Template uses these for prepositional contexts:
   *   PL: locative after "w" / "O" → "w Warszawie", "O Warszawie"
   *       genitive after "dla" → "dla Warszawy"
   *   CS: locative after "v" / "O" → "v Praze", "O Praze"
   * Languages without declension (EN, DE, IT, ES, PT-BR, NL) omit this.
   */
  inflect?: {
    locative?: string;
    genitive?: string;
  };
}

// ── Polish cities (30, in PL locale) ──────────────────────────────────
export const PL_CITIES: CityFacts[] = [
  {
    slug: 'warszawa',
    name: 'Warszawa',
    country: 'Polska',
    locale: 'pl',
    populationK: 1800,
    topIndustries: ['Biura korporacyjne', 'Handel detaliczny', 'Galerie handlowe', 'Banki'],
    climateNote: 'Klimat umiarkowany z silnymi mrozami zimą (−20°C) i upałami latem (+35°C) — wymaga kamer odpornych na ekstremalne temperatury i WDR przeciw refleksom śniegu.',
    regulations: ['GDPR/RODO', 'Ustawa o ochronie danych osobowych', 'EN 50131 Grade 2+', 'PCI-DSS (POS)'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'bosch'],
    intro: 'Warszawa to największy rynek systemów bezpieczeństwa w Polsce — koncentracja siedzib korporacji, banków, galerii handlowych i osiedli mieszkaniowych klasy premium. Wymagania regulacyjne są surowsze niż w mniejszych miastach (RODO, audyty banków, infrastruktura krytyczna).',
    inflect: { locative: 'Warszawie', genitive: 'Warszawy' },
},
  {
    slug: 'krakow',
    name: 'Kraków',
    country: 'Polska',
    locale: 'pl',
    populationK: 800,
    topIndustries: ['Turystyka i hotele', 'IT (Kraków Tech)', 'Centra biznesowe', 'Zabytki'],
    climateNote: 'Smog zimą wpływa na widoczność kamer — kluczowe Smart IR i WDR. Letnie burze wymagają IP66+ obudów.',
    regulations: ['GDPR/RODO', 'Konserwator zabytków (zabudowa historyczna)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Kraków łączy intensywny ruch turystyczny (Stare Miasto, Wawel, Kazimierz) z rosnącym sektorem IT i finansów. Projekty CCTV muszą balansować wymogi konserwatora zabytków (dyskretne instalacje) z gęstością monitoringu w strefach turystycznych.',
    inflect: { locative: 'Krakowie', genitive: 'Krakowa' },
},
  {
    slug: 'lodz',
    name: 'Łódź',
    country: 'Polska',
    locale: 'pl',
    populationK: 670,
    topIndustries: ['Logistyka', 'Centra handlowe', 'Strefy ekonomiczne', 'Akademiki'],
    climateNote: 'Klimat umiarkowany, podobny do Warszawy. Dużo zabudowy poprzemysłowej — potrzebne mocne IR do ciemnych hal.',
    regulations: ['GDPR/RODO', 'Specjalna Strefa Ekonomiczna (audyty)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview'],
    intro: 'Łódź to centrum logistyczne Polski środkowej — gigantyczne magazyny, parki przemysłowe, terminale przeładunkowe. Projekty CCTV skupiają się na perymetrach (kamery bullet z IR 50-100m) i bramach (ANPR).',
    inflect: { locative: 'Łodzi', genitive: 'Łodzi' },
},
  {
    slug: 'wroclaw',
    name: 'Wrocław',
    country: 'Polska',
    locale: 'pl',
    populationK: 670,
    topIndustries: ['Wynajem mieszkań (PRS)', 'Magazyny (Dolnośląska Strefa)', 'IT (Wrocław Tech)', 'Centra biznesowe'],
    climateNote: 'Łagodniejszy klimat niż reszta PL (Niżina Śląska). Sporo opadów wiosennych — obowiązkowy IP66.',
    regulations: ['GDPR/RODO', 'EN 50131 Grade 2', 'DSE (Dolnośląska Strefa Ekonomiczna)'],
    topLocalBrands: ['hikvision', 'hanwha', 'axis'],
    intro: 'Wrocław to drugie po Krakowie centrum IT w Polsce. Projekty CCTV obejmują biurowce klasy A (Sky Tower, Promenady), osiedla premium (Centrum Południowe) i wielkie magazyny w okolicach Bielan Wrocławskich.',
    inflect: { locative: 'Wrocławiu', genitive: 'Wrocławia' },
},
  {
    slug: 'poznan',
    name: 'Poznań',
    country: 'Polska',
    locale: 'pl',
    populationK: 530,
    topIndustries: ['Handel B2B (MTP)', 'Logistyka', 'Banki regionalne', 'Hotele targowe'],
    climateNote: 'Klimat umiarkowany, podobny do Wrocławia. Stosunkowo łagodne zimy.',
    regulations: ['GDPR/RODO', 'EN 50131 Grade 2', 'PCI-DSS'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Poznań to historyczne centrum handlowe (Międzynarodowe Targi Poznańskie) — projekty CCTV obsługują targi B2B, hotele biznesowe, magazyny logistyczne oraz osiedla mieszkaniowe w okolicach Naramowic i Stęszewa.',
    inflect: { locative: 'Poznaniu', genitive: 'Poznania' },
},
  {
    slug: 'gdansk',
    name: 'Gdańsk',
    country: 'Polska',
    locale: 'pl',
    populationK: 470,
    topIndustries: ['Port (Baltic Hub)', 'Petrochemia (Grupa Lotos)', 'Stocznia', 'Hotele nadmorskie'],
    climateNote: 'Klimat morski — wysoka wilgotność, sól w powietrzu. Wymagana obudowa korozji-odporna (stal nierdzewna 316L) IP67.',
    regulations: ['GDPR/RODO', 'Bezpieczeństwo Portów (ISPS)', 'ATEX (rafineria)', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'bosch', 'flir'],
    intro: 'Gdańsk jako port morski i centrum petrochemii ma najbardziej wymagające projekty CCTV w Polsce — perimetry portów (ANPR + thermal), strefy ATEX w rafinerii, monitoring statków. Tu Axis / Bosch / FLIR mają najsilniejszą obecność.',
    inflect: { locative: 'Gdańsku', genitive: 'Gdańska' },
},
  {
    slug: 'szczecin',
    name: 'Szczecin',
    country: 'Polska',
    locale: 'pl',
    populationK: 400,
    topIndustries: ['Port (Szczecin-Świnoujście)', 'Logistyka transgraniczna', 'Wynajem turystyczny'],
    climateNote: 'Klimat morski — wilgoć, opady. Stosunkowo łagodne zimy.',
    regulations: ['GDPR/RODO', 'ISPS (port)', 'Schengen (granica DE)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'axis'],
    intro: 'Szczecin łączy port morski (Szczecin-Świnoujście to drugi port Polski) z bliskością granicy niemieckiej. Projekty CCTV obsługują terminale logistyczne, przejścia graniczne, ANPR na drogach S6/A6.',
    inflect: { locative: 'Szczecinie', genitive: 'Szczecina' },
},
  {
    slug: 'bydgoszcz',
    name: 'Bydgoszcz',
    country: 'Polska',
    locale: 'pl',
    populationK: 340,
    topIndustries: ['Przemysł chemiczny', 'Lotnisko', 'Magazyny', 'Wojsko (Centrum Szkolenia)'],
    climateNote: 'Klimat umiarkowany kontynentalny — chłodne wiatry znad Bałtyku.',
    regulations: ['GDPR/RODO', 'ATEX (chemiczne)', 'Wymagania wojskowe (NATO)', 'EN 50131 Grade 3'],
    topLocalBrands: ['hanwha', 'bosch', 'axis'],
    intro: 'Bydgoszcz ma jeden z trzech głównych ośrodków wojskowych w Polsce (NATO Joint Force Training Centre) — projekty CCTV muszą spełniać wymogi NATO, sieci klasy SECRET, EN 50131 Grade 3.',
    inflect: { locative: 'Bydgoszczy', genitive: 'Bydgoszczy' },
},
  {
    slug: 'lublin',
    name: 'Lublin',
    country: 'Polska',
    locale: 'pl',
    populationK: 340,
    topIndustries: ['Akademiki (uniwersytety)', 'Centra handlowe', 'Logistyka wschodu', 'Rolnictwo'],
    climateNote: 'Klimat kontynentalny — gorętsze lata, ostre zimy. Smog w sezonie grzewczym.',
    regulations: ['GDPR/RODO', 'Schengen (granica UA)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Lublin to brama na Ukrainę — terminale graniczne, logistyka transgraniczna, centra dystrybucyjne. Po inwazji 2022 wzrósł popyt na systemy bezpieczeństwa, monitoring granicy, ochronę uchodźców.',
    inflect: { locative: 'Lublinie', genitive: 'Lublina' },
},
  {
    slug: 'katowice',
    name: 'Katowice',
    country: 'Polska',
    locale: 'pl',
    populationK: 290,
    topIndustries: ['Górnictwo (kopalnie)', 'Energetyka', 'Centra targowe (MCK)', 'Konferencje'],
    climateNote: 'Wysoka koncentracja pyłu (przemysł, węgiel) — wymagane IP67 + auto-czyszczenie soczewek.',
    regulations: ['GDPR/RODO', 'ATEX (kopalnie)', 'Górnictwo (BHP)', 'EN 50131 Grade 3'],
    topLocalBrands: ['bosch', 'hanwha', 'axis'],
    intro: 'Katowice (i cała aglomeracja Górnego Śląska) to specjalistyczny rynek — kopalnie, koksownie, elektrownie. Projekty CCTV wymagają obudów ATEX (strefa Ex), thermal imaging dla kopalń, gęstej redundancji.',
    inflect: { locative: 'Katowicach', genitive: 'Katowic' },
},
  {
    slug: 'bialystok',
    name: 'Białystok',
    country: 'Polska',
    locale: 'pl',
    populationK: 290,
    topIndustries: ['Granica wschodnia', 'Drewno i meble', 'Akademiki', 'Logistyka'],
    climateNote: 'Najzimniejsza Polska — temperatury −25°C nierzadkie. Kamery muszą być −40°C rated, podgrzewane.',
    regulations: ['GDPR/RODO', 'Schengen (granica BY)', 'Strefa nadgraniczna', 'EN 50131 Grade 2'],
    topLocalBrands: ['hanwha', 'axis', 'hikvision'],
    intro: 'Białystok jako miasto graniczne z Białorusią ma podwyższoną wagę przejść granicznych i monitoringu strefy nadgranicznej. Projekty CCTV muszą działać w ekstremalnym chłodzie i pokrywać duże perymetry.',
    inflect: { locative: 'Białymstoku', genitive: 'Białegostoku' },
},
  {
    slug: 'gdynia',
    name: 'Gdynia',
    country: 'Polska',
    locale: 'pl',
    populationK: 240,
    topIndustries: ['Port morski', 'Marina (mariny jachtowe)', 'Stocznia', 'Wojsko (MW)'],
    climateNote: 'Klimat morski jak Gdańsk — wilgoć, sól. Wymagana stal 316L IP67.',
    regulations: ['GDPR/RODO', 'ISPS', 'Wymagania MW (NATO)', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'bosch', 'flir'],
    intro: 'Gdynia jako baza Marynarki Wojennej + komercyjny port + mariny jachtowe ma 3 odmienne strefy bezpieczeństwa — każda z innymi wymogami compliance. NATO security clearance dla MW; cywilne projekty dla portu i marin.',
    inflect: { locative: 'Gdyni', genitive: 'Gdyni' },
},
  {
    slug: 'czestochowa',
    name: 'Częstochowa',
    country: 'Polska',
    locale: 'pl',
    populationK: 220,
    topIndustries: ['Sanktuarium Jasnogórskie (turystyka religijna)', 'Hutnictwo', 'Tekstylia'],
    climateNote: 'Klimat kontynentalny, podobny do Katowic. Smog zimą.',
    regulations: ['GDPR/RODO', 'Sanktuaria (specjalne)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Częstochowa to największy ośrodek pielgrzymek w Polsce (5+ mln pielgrzymów/rok) — projekty CCTV dla Sanktuarium Jasnogórskiego, hoteli pielgrzymkowych, parkingów masowych podczas uroczystości religijnych.',
    inflect: { locative: 'Częstochowie', genitive: 'Częstochowy' },
},
  {
    slug: 'radom',
    name: 'Radom',
    country: 'Polska',
    locale: 'pl',
    populationK: 220,
    topIndustries: ['Lotnisko (LOT Polish Charters)', 'Broń (Bumar)', 'Skóra i obuwie'],
    climateNote: 'Klimat kontynentalny — chłodne wiatry północne.',
    regulations: ['GDPR/RODO', 'Bezpieczeństwo lotnicze', 'Wymagania zbrojeniówki', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Radom ma lotnisko regionalne + zakłady zbrojeniowe Bumar — projekty CCTV obejmują strefy lotniska (perimeter + airside) i obiekty zbrojeniowe (Grade 3, NATO standards).',
    inflect: { locative: 'Radomiu', genitive: 'Radomia' },
},
  {
    slug: 'sosnowiec',
    name: 'Sosnowiec',
    country: 'Polska',
    locale: 'pl',
    populationK: 200,
    topIndustries: ['Górnictwo zakończone — teraz logistyka i handel', 'Centra handlowe', 'Magazyny'],
    climateNote: 'Smog jak Katowice. Pył pogórniczy w dawnych terenach.',
    regulations: ['GDPR/RODO', 'Rekultywacja (dawne kopalnie)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview'],
    intro: 'Sosnowiec w postgórniczej transformacji — projekty CCTV obsługują nowo wybudowane centra handlowe, magazyny logistyczne na pogórniczych terenach, osiedla mieszkaniowe.',
    inflect: { locative: 'Sosnowcu', genitive: 'Sosnowca' },
},
  {
    slug: 'torun',
    name: 'Toruń',
    country: 'Polska',
    locale: 'pl',
    populationK: 200,
    topIndustries: ['Turystyka (UNESCO)', 'Uniwersytet', 'Przemysł spożywczy (piernikarstwo)'],
    climateNote: 'Klimat umiarkowany — bliskość Wisły wpływa na wilgotność.',
    regulations: ['GDPR/RODO', 'UNESCO (zabytki)', 'Konserwator', 'EN 50131 Grade 2'],
    topLocalBrands: ['hanwha', 'hikvision', 'dahua'],
    intro: 'Toruń jako miasto UNESCO ma szczególnie surowe wymogi konserwatorskie — dyskretne kamery, brak widocznych instalacji w starówce. Uniwersytet Mikołaja Kopernika to znaczący klient (akademiki, biblioteki).',
    inflect: { locative: 'Toruniu', genitive: 'Torunia' },
},
  {
    slug: 'kielce',
    name: 'Kielce',
    country: 'Polska',
    locale: 'pl',
    populationK: 190,
    topIndustries: ['Targi (Targi Kielce)', 'Hotele konferencyjne', 'Hutnictwo (Celsa)'],
    climateNote: 'Klimat kontynentalny — gorętsze lata.',
    regulations: ['GDPR/RODO', 'Bezpieczeństwo imprez masowych', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Kielce to drugie po Poznaniu centrum targowe Polski (Targi Kielce, MSPO defence expo) — projekty CCTV obsługują halę targową, hotele biznesowe, ANPR na parkingach masowych.',
    inflect: { locative: 'Kielcach', genitive: 'Kielc' },
},
  {
    slug: 'rzeszow',
    name: 'Rzeszów',
    country: 'Polska',
    locale: 'pl',
    populationK: 200,
    topIndustries: ['Aviation Valley (lotnictwo)', 'Lotnisko Jasionka', 'IT', 'Logistyka wschód'],
    climateNote: 'Klimat kontynentalny — kontynentalne lato.',
    regulations: ['GDPR/RODO', 'Bezpieczeństwo lotnicze', 'EASA', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Rzeszów-Jasionka stał się głównym hubem logistycznym dla pomocy Ukrainie (2022+) — projekty CCTV dramatycznie urosły. Aviation Valley to klaster firm lotniczych z Grade 3 EN 50131 wymogami.',
    inflect: { locative: 'Rzeszowie', genitive: 'Rzeszowa' },
},
  {
    slug: 'gliwice',
    name: 'Gliwice',
    country: 'Polska',
    locale: 'pl',
    populationK: 180,
    topIndustries: ['Politechnika Śląska', 'Centra IT', 'Logistyka A4', 'Opel/Stellantis (montownia)'],
    climateNote: 'Klimat industrialny — smog, ale przemysł czyściejszy niż Katowice.',
    regulations: ['GDPR/RODO', 'Specjalna Strefa Ekonomiczna', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'hanwha', 'bosch'],
    intro: 'Gliwice to ośrodek IT (Politechnika Śląska) + montownia Opla/Stellantis. Projekty CCTV obejmują biurowce klasy A, fabrykę motoryzacyjną (perimetry kilometrów ogrodzeń), akademiki.',
    inflect: { locative: 'Gliwicach', genitive: 'Gliwic' },
},
  {
    slug: 'zabrze',
    name: 'Zabrze',
    country: 'Polska',
    locale: 'pl',
    populationK: 170,
    topIndustries: ['Medycyna (Śląskie Centrum Chorób Serca)', 'Górnictwo zakończone', 'Kongresy'],
    climateNote: 'Smog Katowic. Pył pogórniczy.',
    regulations: ['GDPR/RODO', 'HIPAA-podobne (Polska)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hanwha', 'bosch', 'axis'],
    intro: 'Zabrze ma jeden z czołowych ośrodków kardiochirurgii w Europie (transplantacje serca) — szpitalne projekty CCTV z ograniczeniami prywatności pacjentów, dual-control dostęp do farmaceutyków.',
    inflect: { locative: 'Zabrzu', genitive: 'Zabrza' },
},
  {
    slug: 'olsztyn',
    name: 'Olsztyn',
    country: 'Polska',
    locale: 'pl',
    populationK: 170,
    topIndustries: ['Uniwersytet', 'Mazury (turystyka)', 'Pojezierze', 'Logistyka'],
    climateNote: 'Chłodne klimat pojezierski. Wysoka wilgotność (jeziora).',
    regulations: ['GDPR/RODO', 'Park Krajobrazowy (chronione)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Olsztyn to brama do Mazur — projekty CCTV obsługują hotele nadwodne, mariny jachtowe, domki letniskowe, kemping. Sezonowość projektów (wiosna-jesień).',
    inflect: { locative: 'Olsztynie', genitive: 'Olsztyna' },
},
  {
    slug: 'bielsko-biala',
    name: 'Bielsko-Biała',
    country: 'Polska',
    locale: 'pl',
    populationK: 170,
    topIndustries: ['Fiat (FCA Poland)', 'Beskidy (turystyka górska)', 'Lotnictwo (3xtrim)'],
    climateNote: 'Klimat górski — opady, śnieg zimą.',
    regulations: ['GDPR/RODO', 'Sektor motoryzacyjny (IATF 16949)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'hanwha', 'bosch'],
    intro: 'Bielsko-Biała koncentruje montownię Fiata (Fiat Tipo, Jeep Avenger) — projekty CCTV obejmują fabrykę motoryzacyjną z perymetrami kilometrów ogrodzeń, hotele górskie, narciarskie ośrodki w okolicach.',
    inflect: { locative: 'Bielsku-Białej', genitive: 'Bielska-Białej' },
},
  {
    slug: 'bytom',
    name: 'Bytom',
    country: 'Polska',
    locale: 'pl',
    populationK: 160,
    topIndustries: ['Postgórniczy (restrukturyzacja)', 'Centra handlowe', 'Logistyka'],
    climateNote: 'Pył pogórniczy. Smog. Wysoka wilgotność dolnośląska.',
    regulations: ['GDPR/RODO', 'Rekultywacja terenów', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview'],
    intro: 'Bytom w głębokiej postgórniczej transformacji — projekty CCTV pomagają wyciągnąć inwestycje do nowych galerii handlowych, magazynów, obiektów mieszkaniowych na pogórniczych terenach.',
    inflect: { locative: 'Bytomiu', genitive: 'Bytomia' },
},
  {
    slug: 'zielona-gora',
    name: 'Zielona Góra',
    country: 'Polska',
    locale: 'pl',
    populationK: 140,
    topIndustries: ['Winnice (jedyne polskie regionalne)', 'Akademiki (Uniwersytet)', 'Logistyka A18'],
    climateNote: 'Łagodny klimat (najcieplejszy w Polsce). Mało zim.',
    regulations: ['GDPR/RODO', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'satel'],
    intro: 'Zielona Góra to nietypowy rynek — winnice (zielonogórskie wino), uniwersytet, logistyka transgraniczna z Niemcami. Projekty CCTV pomagają chronić infrastrukturę winnic (kradzieże gron) i obiekty akademickie.',
    inflect: { locative: 'Zielonej Górze', genitive: 'Zielonej Góry' },
},
  {
    slug: 'rybnik',
    name: 'Rybnik',
    country: 'Polska',
    locale: 'pl',
    populationK: 140,
    topIndustries: ['Energetyka (Elektrownia Rybnik)', 'Górnictwo zakończone', 'Kąpieliska'],
    climateNote: 'Smog. Pył energetyczny.',
    regulations: ['GDPR/RODO', 'ATEX (energetyka)', 'EN 50131 Grade 3'],
    topLocalBrands: ['bosch', 'hanwha', 'axis'],
    intro: 'Rybnik z gigantyczną elektrownią węglową — projekty CCTV obejmują strefy ATEX (skład węgla, młyny), perimetry kilometrów ogrodzeń, dostęp do sterowni.',
    inflect: { locative: 'Rybniku', genitive: 'Rybnika' },
},
  {
    slug: 'ruda-slaska',
    name: 'Ruda Śląska',
    country: 'Polska',
    locale: 'pl',
    populationK: 140,
    topIndustries: ['Postgórniczy', 'Hutnictwo', 'Logistyka'],
    climateNote: 'Smog Katowic. Pył pogórniczy.',
    regulations: ['GDPR/RODO', 'Rekultywacja', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview'],
    intro: 'Ruda Śląska postgórnicza — projekty CCTV pomagają w nowych inwestycjach magazynowych i mieszkaniowych na zrekultywowanych terenach.',
    inflect: { locative: 'Rudzie Śląskiej', genitive: 'Rudy Śląskiej' },
},
  {
    slug: 'opole',
    name: 'Opole',
    country: 'Polska',
    locale: 'pl',
    populationK: 130,
    topIndustries: ['Cementownia (Opole Cement)', 'Energetyka (Elektrownia Opole)', 'Targi'],
    climateNote: 'Klimat dolnośląski — łagodniejszy. Pył cementowy lokalnie.',
    regulations: ['GDPR/RODO', 'ATEX', 'EN 50131 Grade 3'],
    topLocalBrands: ['bosch', 'hanwha', 'axis'],
    intro: 'Opole z cementownią i elektrownią to przemysłowy klient — projekty CCTV koncentrują się na halach produkcyjnych (IP67 przeciw pyłowi), perimetrach, monitoringu emisji.',
    inflect: { locative: 'Opolu', genitive: 'Opola' },
},
  {
    slug: 'tychy',
    name: 'Tychy',
    country: 'Polska',
    locale: 'pl',
    populationK: 120,
    topIndustries: ['Fiat (zakład tyski)', 'Browary (Tyskie)', 'Logistyka A1', 'Centra handlowe'],
    climateNote: 'Smog Katowic.',
    regulations: ['GDPR/RODO', 'IATF 16949 (motoryzacja)', 'HACCP (browar)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'hanwha', 'bosch'],
    intro: 'Tychy łączy montownię Fiata + browar Tyskie + galerie handlowe. Projekty CCTV obejmują line cameras w fabryce, monitoring obrotu opakowań w browarze, ANPR przy bramach.',
    inflect: { locative: 'Tychach', genitive: 'Tychów' },
},
  {
    slug: 'gorzow-wielkopolski',
    name: 'Gorzów Wielkopolski',
    country: 'Polska',
    locale: 'pl',
    populationK: 120,
    topIndustries: ['Logistyka transgraniczna (DE)', 'Sieć handlowa', 'Hotele tranzytowe'],
    climateNote: 'Łagodny klimat zachodni. Sporo wiatrów.',
    regulations: ['GDPR/RODO', 'Schengen (granica DE)', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Gorzów na trasie do Berlina — projekty CCTV obsługują parkingi tranzytowe (ANPR), hotele dla kierowców TIR, centra logistyczne korzystające z bliskości granicy niemieckiej.',
    inflect: { locative: 'Gorzowie Wielkopolskim', genitive: 'Gorzowa Wielkopolskiego' },
},
  {
    slug: 'dabrowa-gornicza',
    name: 'Dąbrowa Górnicza',
    country: 'Polska',
    locale: 'pl',
    populationK: 120,
    topIndustries: ['Hutnictwo (ArcelorMittal)', 'Postgórniczy', 'Logistyka'],
    climateNote: 'Smog hutniczy. Pył.',
    regulations: ['GDPR/RODO', 'ATEX', 'EN 50131 Grade 2'],
    topLocalBrands: ['bosch', 'hanwha', 'hikvision'],
    intro: 'Dąbrowa Górnicza z największą hutą stali w Polsce (ArcelorMittal) — projekty CCTV obejmują strefy ATEX, halę walcowniczą (thermal!), perimetry kilometrów ogrodzeń.',
    inflect: { locative: 'Dąbrowie Górniczej', genitive: 'Dąbrowy Górniczej' },
},
];

// ── EN-language cities (30: UK + US) ──────────────────────────────────
export const EN_CITIES: CityFacts[] = [
  // UK
  {
    slug: 'london',
    name: 'London',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 9000,
    topIndustries: ['Financial services (The City)', 'Government & embassies', 'Retail (Oxford Street)', 'Transit hubs'],
    climateNote: 'Mild temperate climate — high humidity year-round, rare freezing. IP66 cameras with weatherproof housings.',
    regulations: ['UK GDPR', 'PCI-DSS', 'Surveillance Camera Code of Practice (Home Office)', 'EN 50131 Grade 2-3'],
    topLocalBrands: ['axis', 'hanwha', 'bosch', 'hikvision'],
    intro: 'London is the most surveillance-camera-dense city in the EU — over 1 million cameras across public/private installs. Projects span banking towers in The City, government buildings, retail flagships, and the Tube network. Strict Surveillance Camera Code (Home Office) governs public-facing deployments.',
  },
  {
    slug: 'manchester',
    name: 'Manchester',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 2800,
    topIndustries: ['Manufacturing', 'University estates', 'Retail (Trafford Centre)', 'Logistics (Trafford Park)'],
    climateNote: 'Rainfall capital of the UK — IP67 mandatory, weatherproof housings preferred over IP66.',
    regulations: ['UK GDPR', 'EN 50131 Grade 2', 'PCI-DSS'],
    topLocalBrands: ['hanwha', 'axis', 'hikvision'],
    intro: 'Manchester combines heavy manufacturing (Trafford Park is one of Europe\'s largest industrial estates), four major universities, and the Trafford Centre retail anchor. Projects favour wired PoE installs over wireless due to chronic damp.',
  },
  {
    slug: 'birmingham',
    name: 'Birmingham',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 2900,
    topIndustries: ['Automotive (Jaguar Land Rover)', 'Financial back-office', 'NEC (conferences)', 'Universities'],
    climateNote: 'Mild temperate. Industrial pollution in the Black Country requires IP67 + auto-cleaning lenses.',
    regulations: ['UK GDPR', 'IATF 16949 (automotive)', 'EN 50131 Grade 2-3'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Birmingham is the UK\'s second-largest city and the heart of the automotive industry (Jaguar Land Rover, BMW Mini). Projects span auto-manufacturing plants, financial back-offices, and the NEC conference complex.',
  },
  {
    slug: 'leeds',
    name: 'Leeds',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 800,
    topIndustries: ['Finance (Asda HQ, banks)', 'Universities', 'Retail (Trinity Leeds)', 'Logistics'],
    climateNote: 'Yorkshire weather — wet, windy, occasional snow. Heated camera housings for winter.',
    regulations: ['UK GDPR', 'PCI-DSS', 'EN 50131 Grade 2'],
    topLocalBrands: ['hanwha', 'axis', 'hikvision'],
    intro: 'Leeds is the UK\'s northern financial capital — major banks (HSBC, Lloyds), Asda HQ, retail and conference centres. Projects target multi-tenant office towers and retail anchor stores.',
  },
  {
    slug: 'glasgow',
    name: 'Glasgow',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 600,
    topIndustries: ['Shipbuilding (BAE)', 'Whiskey distilleries', 'Universities', 'Conferences (SEC)'],
    climateNote: 'Wettest major UK city — IP67 + corrosion-resistant housings essential. Salt air near docks.',
    regulations: ['UK GDPR', 'EN 50131 Grade 3 (military)', 'HACCP (food/drink)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Glasgow combines BAE shipbuilding (Royal Navy contracts) with major whiskey distilleries and the Scottish Event Campus. Military-grade compliance for defence projects; HACCP for distilleries.',
  },
  {
    slug: 'edinburgh',
    name: 'Edinburgh',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 530,
    topIndustries: ['Financial services (RBS HQ)', 'Government (Scottish Parliament)', 'Tourism (UNESCO)', 'Festivals'],
    climateNote: 'Cool temperate, frequent fog — Smart IR critical for night visibility.',
    regulations: ['UK GDPR', 'PCI-DSS', 'UNESCO heritage', 'EN 50131 Grade 2-3'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Edinburgh hosts the Scottish Parliament, Royal Bank of Scotland HQ, and the Edinburgh International Festival (peak crowds). UNESCO old town heritage status restricts visible camera placement; discrete installs preferred.',
  },
  {
    slug: 'liverpool',
    name: 'Liverpool',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 500,
    topIndustries: ['Port (Liverpool Docks)', 'Tourism', 'Universities', 'Logistics'],
    climateNote: 'Maritime climate — salt corrosion concerns. Stainless steel IP67 housings.',
    regulations: ['UK GDPR', 'ISPS (port security)', 'EN 50131 Grade 2-3'],
    topLocalBrands: ['axis', 'bosch', 'hikvision'],
    intro: 'Liverpool port handles 33 million tonnes/year — ISPS port-security regulations apply. Projects span container terminals (ANPR + thermal), city centre retail, and Beatles tourism sites.',
  },
  {
    slug: 'bristol',
    name: 'Bristol',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 470,
    topIndustries: ['Aerospace (Airbus)', 'Tech (University of Bristol)', 'Harbourside', 'Tourism'],
    climateNote: 'Mild maritime climate — mostly wet, occasional summer heatwaves.',
    regulations: ['UK GDPR', 'Aerospace ITAR', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Bristol is the UK\'s aerospace capital (Airbus Filton, Rolls-Royce). Aerospace projects require ITAR-compliant supply chain — NDAA-banned brands (Hikvision, Dahua) often disqualified by customer policy.',
  },
  {
    slug: 'sheffield',
    name: 'Sheffield',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 580,
    topIndustries: ['Steel (legacy)', 'Universities (Sheffield, Hallam)', 'Advanced manufacturing', 'Retail'],
    climateNote: 'Cool temperate. Industrial dust legacy in older areas.',
    regulations: ['UK GDPR', 'EN 50131 Grade 2', 'IATF 16949'],
    topLocalBrands: ['hanwha', 'axis', 'hikvision'],
    intro: 'Sheffield transitioned from steel to advanced manufacturing (AMRC research) and student-heavy retail. Projects target university campuses, retail centres, and remaining specialty steel facilities.',
  },
  {
    slug: 'belfast',
    name: 'Belfast',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 340,
    topIndustries: ['Shipbuilding (Harland & Wolff)', 'Tech (Titanic Quarter)', 'Tourism', 'Universities'],
    climateNote: 'Wet maritime. Sea air corrosion concerns at docks.',
    regulations: ['UK GDPR', 'Northern Ireland Protocol', 'ISPS (port)', 'EN 50131 Grade 2-3'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Belfast combines historic shipbuilding (where Titanic was built) with new tech hubs in the Titanic Quarter. Cross-border compliance with Republic of Ireland adds complexity for some projects.',
  },
  {
    slug: 'leicester',
    name: 'Leicester',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 350,
    topIndustries: ['Textile manufacturing', 'Universities', 'Logistics (M1 corridor)', 'Retail'],
    climateNote: 'Mild temperate. Occasional industrial dust.',
    regulations: ['UK GDPR', 'EN 50131 Grade 2', 'Modern Slavery Act audits'],
    topLocalBrands: ['hanwha', 'hikvision', 'dahua'],
    intro: 'Leicester is the UK\'s textile manufacturing capital, with Modern Slavery Act compliance pressure pushing video audit trails in factories. Universities + logistics complete the local mix.',
  },
  {
    slug: 'coventry',
    name: 'Coventry',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 370,
    topIndustries: ['Automotive (Jaguar Land Rover HQ)', 'Universities', 'Logistics'],
    climateNote: 'Mild temperate. Industrial environments inside JLR plants.',
    regulations: ['UK GDPR', 'IATF 16949', 'EN 50131 Grade 2'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Coventry is Jaguar Land Rover\'s global HQ — automotive projects dominate. Aftermarket dealer network and Coventry University also drive local CCTV demand.',
  },
  {
    slug: 'newcastle',
    name: 'Newcastle',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 300,
    topIndustries: ['Tech (Tyne & Wear)', 'Universities', 'Retail (Eldon Square)', 'Quayside hospitality'],
    climateNote: 'Cool maritime — windy, salt air. Heated housings for winter.',
    regulations: ['UK GDPR', 'EN 50131 Grade 2', 'PCI-DSS'],
    topLocalBrands: ['hanwha', 'hikvision', 'axis'],
    intro: 'Newcastle tech corridor (Newcastle and Northumbria universities) drives campus security demand. Quayside hospitality and Eldon Square retail anchor commercial CCTV projects.',
  },
  {
    slug: 'southampton',
    name: 'Southampton',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 270,
    topIndustries: ['Port (cruise terminals)', 'Aerospace (BAE)', 'Universities', 'Logistics'],
    climateNote: 'Mild maritime. Salt corrosion at docks.',
    regulations: ['UK GDPR', 'ISPS (port)', 'ITAR (aerospace)', 'EN 50131 Grade 3'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Southampton is the UK\'s busiest cruise port and major container hub. ISPS compliance critical; ITAR for aerospace; both push NDAA-compliant brands.',
  },
  {
    slug: 'cardiff',
    name: 'Cardiff',
    country: 'United Kingdom',
    locale: 'en',
    populationK: 365,
    topIndustries: ['Government (Welsh Senedd)', 'Universities', 'Stadium (Principality)', 'Tech'],
    climateNote: 'Wet maritime. Cardiff Bay marine air.',
    regulations: ['UK GDPR', 'Welsh government privacy guidance', 'EN 50131 Grade 2'],
    topLocalBrands: ['axis', 'hanwha', 'hikvision'],
    intro: 'Cardiff houses the Welsh Senedd (government) and Principality Stadium — security projects span government, large-event stadium, and growing tech hub.',
  },
  // US
  {
    slug: 'new-york',
    name: 'New York City',
    country: 'United States',
    locale: 'en',
    populationK: 8400,
    topIndustries: ['Financial services (Wall Street)', 'Retail (5th Ave)', 'Hotels', 'Transit (MTA)'],
    climateNote: 'Continental — −20°C winters to 35°C summers. Snow load on outdoor housings. Salt at coast.',
    regulations: ['NDAA Section 889', 'NIST 800-171 (federal contracts)', 'PCI-DSS', 'NYC Earned Sick Leave audits', 'SHIELD Act'],
    topLocalBrands: ['axis', 'hanwha', 'bosch', 'pelco'],
    intro: 'NYC is the most demanding CCTV market in the US — NDAA-compliant brands only for federal/state contracts; SHIELD Act privacy + extensive NYPD oversight on public-facing cameras. Banking (Wall Street), retail flagships, MTA transit, and hotel chains drive demand.',
  },
  {
    slug: 'los-angeles',
    name: 'Los Angeles',
    country: 'United States',
    locale: 'en',
    populationK: 4000,
    topIndustries: ['Entertainment (studios)', 'Port (Long Beach/LA)', 'Aerospace', 'Tourism'],
    climateNote: 'Mediterranean — mostly warm, dry. Wildfire smoke affects camera visibility in late summer.',
    regulations: ['NDAA Section 889', 'CCPA (California privacy)', 'ITAR (aerospace)', 'ISPS (port)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'LA combines Hollywood studios (high-value content protection), Port of LA/Long Beach (largest US port complex), aerospace (Northrop, Lockheed), and tourism. CCPA adds strict data subject rights on top of federal requirements.',
  },
  {
    slug: 'chicago',
    name: 'Chicago',
    country: 'United States',
    locale: 'en',
    populationK: 2700,
    topIndustries: ['Financial services (CME, banks)', 'Manufacturing', 'O\'Hare Airport', 'Logistics'],
    climateNote: 'Continental extremes — −25°C winters, +35°C summers, lake-effect snow. Heated housings essential.',
    regulations: ['NDAA Section 889', 'Illinois BIPA (biometrics)', 'PCI-DSS', 'EN 50131-equivalent UL 681'],
    topLocalBrands: ['axis', 'hanwha', 'bosch', 'pelco'],
    intro: 'Chicago is the US logistics capital + financial centre (CME futures, major banks). Illinois BIPA is the strictest US biometric privacy law — facial recognition deployments face million-dollar lawsuits.',
  },
  {
    slug: 'houston',
    name: 'Houston',
    country: 'United States',
    locale: 'en',
    populationK: 2300,
    topIndustries: ['Oil & gas (Houston Energy)', 'Medical (Texas Medical Center)', 'Port (Galveston)', 'NASA'],
    climateNote: 'Hot subtropical — humidity ruins consumer-grade housings. Hurricane risk requires hardened mounts.',
    regulations: ['NDAA Section 889', 'NIST 800-171', 'HIPAA (medical)', 'ATEX-equivalent (oil/gas hazardous)'],
    topLocalBrands: ['axis', 'bosch', 'pelco', 'flir'],
    intro: 'Houston is the global oil & gas capital + Texas Medical Center (the world\'s largest medical complex). Oil/gas demands ATEX-equivalent hazardous-zone cameras; medical demands HIPAA compliance; both push enterprise-tier brands.',
  },
  {
    slug: 'phoenix',
    name: 'Phoenix',
    country: 'United States',
    locale: 'en',
    populationK: 1700,
    topIndustries: ['Data centers (cool dry desert)', 'Semiconductor (TSMC Arizona)', 'Tourism (Scottsdale)', 'Logistics'],
    climateNote: 'Hot dry desert — +45°C summers cook electronics. Wide-temperature-rated cameras essential.',
    regulations: ['NDAA Section 889', 'NIST 800-171 (TSMC supply chain)', 'PCI-DSS', 'SOC 2 (data centers)'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Phoenix has become the US semiconductor capital (TSMC fab investment) and data center hub. Tier III/IV data centers need extreme security: dual-control access, biometric mantraps, full aisle CCTV.',
  },
  {
    slug: 'philadelphia',
    name: 'Philadelphia',
    country: 'United States',
    locale: 'en',
    populationK: 1600,
    topIndustries: ['Universities (Penn, Drexel)', 'Healthcare (UPenn Hospital)', 'Pharma (GSK, J&J)', 'Port'],
    climateNote: 'Continental — humid summers, cold winters. Lake-effect-adjacent snow.',
    regulations: ['NDAA Section 889', 'HIPAA', 'FDA 21 CFR Part 11 (pharma)', 'EN 50131-equivalent UL 681'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Philadelphia has major universities, pharma (J&J, GSK), and the busiest port on the US East Coast (PhilaPort). HIPAA and FDA pharma compliance push NDAA-compliant brands.',
  },
  {
    slug: 'san-antonio',
    name: 'San Antonio',
    country: 'United States',
    locale: 'en',
    populationK: 1500,
    topIndustries: ['Military (Joint Base SA)', 'Healthcare (medical center)', 'Tourism (River Walk)', 'Cybersecurity'],
    climateNote: 'Hot subtropical — humid summers. Mild winters.',
    regulations: ['NDAA Section 889 (military)', 'NIST 800-171', 'HIPAA', 'CMMC (defense contractors)'],
    topLocalBrands: ['axis', 'bosch', 'pelco'],
    intro: 'San Antonio hosts Joint Base San Antonio (largest single DoD installation in the US) + Cyber Command (NSA-related). CMMC + NIST 800-171 compliance mandatory for any defence-adjacent project.',
  },
  {
    slug: 'san-diego',
    name: 'San Diego',
    country: 'United States',
    locale: 'en',
    populationK: 1400,
    topIndustries: ['Military (Naval Base)', 'Biotech (Qualcomm)', 'Border (TJ crossing)', 'Tourism'],
    climateNote: 'Coastal Mediterranean — mild year-round. Salt corrosion near naval base.',
    regulations: ['NDAA Section 889', 'CCPA', 'CMMC', 'CBP (border)', 'HIPAA (biotech)'],
    topLocalBrands: ['axis', 'bosch', 'flir'],
    intro: 'San Diego combines Navy base (largest US naval base outside Norfolk) with biotech (Qualcomm, Illumina). FLIR thermal cameras strong here due to border + naval applications.',
  },
  {
    slug: 'dallas',
    name: 'Dallas',
    country: 'United States',
    locale: 'en',
    populationK: 1300,
    topIndustries: ['Financial services', 'Telecom (AT&T)', 'Healthcare', 'Logistics (DFW airport)'],
    climateNote: 'Hot subtropical — extreme summer heat. Tornado risk requires hardened mounts.',
    regulations: ['NDAA Section 889', 'NIST 800-171', 'PCI-DSS', 'HIPAA'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Dallas is a major US financial back-office hub + telecom (AT&T HQ) + DFW airport. Financial compliance (NIST, PCI-DSS) and aviation security drive enterprise-tier brand selection.',
  },
  {
    slug: 'austin',
    name: 'Austin',
    country: 'United States',
    locale: 'en',
    populationK: 1000,
    topIndustries: ['Tech (Apple, Tesla)', 'Government (Texas Capitol)', 'Universities (UT Austin)', 'Music venues'],
    climateNote: 'Hot subtropical — extreme summer heat. Severe weather risk.',
    regulations: ['NDAA Section 889', 'NIST 800-171', 'CCPA-equivalent (Texas)', 'PCI-DSS'],
    topLocalBrands: ['axis', 'ubiquiti', 'hanwha'],
    intro: 'Austin is the fastest-growing US tech hub (Apple, Tesla, Oracle relocated HQs). Mix of tech campuses + government + UT Austin university drives enterprise + prosumer (Ubiquiti) brand demand.',
  },
  {
    slug: 'boston',
    name: 'Boston',
    country: 'United States',
    locale: 'en',
    populationK: 700,
    topIndustries: ['Universities (Harvard, MIT)', 'Healthcare (Mass General)', 'Biotech (Kendall Sq)', 'Finance'],
    climateNote: 'Continental — cold snowy winters, humid summers. Heated housings + snow load.',
    regulations: ['NDAA Section 889', 'Mass General Laws Ch. 214', 'HIPAA', 'NIST 800-171'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Boston combines world-class universities, the densest biotech cluster in the world (Kendall Square), and major hospitals (Mass General, Brigham). Research IP protection + HIPAA compliance push NDAA-compliant brands.',
  },
  {
    slug: 'seattle',
    name: 'Seattle',
    country: 'United States',
    locale: 'en',
    populationK: 750,
    topIndustries: ['Tech (Amazon, Microsoft)', 'Aerospace (Boeing)', 'Port (Seattle)', 'Coffee (Starbucks HQ)'],
    climateNote: 'Maritime — wet year-round. Mild temperatures. Salt air near port.',
    regulations: ['NDAA Section 889', 'CCPA-equivalent (Washington)', 'ITAR (Boeing)', 'ISPS (port)'],
    topLocalBrands: ['axis', 'ubiquiti', 'hanwha'],
    intro: 'Seattle has Amazon + Microsoft HQs + Boeing manufacturing + the Port of Seattle. Tech + aerospace combines for strict ITAR/NIST compliance; wet climate demands IP67 + corrosion-resistant housings.',
  },
  {
    slug: 'miami',
    name: 'Miami',
    country: 'United States',
    locale: 'en',
    populationK: 470,
    topIndustries: ['Tourism (hotels)', 'Latin American finance', 'Port (cruise)', 'Real estate'],
    climateNote: 'Hot humid tropical — salt air, hurricanes, daily afternoon storms. IP67 + corrosion-resistant essential.',
    regulations: ['NDAA Section 889', 'Florida CS/HB 9 (privacy)', 'PCI-DSS', 'BSA/AML (Latin American finance)'],
    topLocalBrands: ['axis', 'hanwha', 'bosch'],
    intro: 'Miami is the gateway to Latin American finance — strict BSA/AML compliance for international banks. Hurricane-rated mounts and saltwater-resistant housings are non-negotiable.',
  },
  {
    slug: 'washington-dc',
    name: 'Washington, D.C.',
    country: 'United States',
    locale: 'en',
    populationK: 700,
    topIndustries: ['Federal government', 'Defence contractors', 'Universities (Georgetown, GW)', 'Embassies'],
    climateNote: 'Humid continental — hot summers, cold winters. Snow.',
    regulations: ['NDAA Section 889 (mandatory)', 'NIST 800-171', 'CMMC', 'Classified handling (SCIF)', 'TSCM'],
    topLocalBrands: ['axis', 'bosch', 'pelco'],
    intro: 'D.C. is the strictest US security market — federal contracts mandate NDAA-compliant equipment, classified facilities (SCIF) require TEMPEST-rated hardware. Hikvision/Dahua absolutely disqualified.',
  },
];

// ── German cities (Sprint A — 10 cities, DE locale) ───────────────────
// Slug pattern: /de/cctv-planung-{slug}
// DACH market is the largest EU CCTV market by revenue (~€3-4B/yr) and
// currently underserved by us: 3 paid subs vs USA's 8. Highest-ROI sprint.
export const DE_CITIES: CityFacts[] = [
  {
    slug: 'berlin',
    name: 'Berlin',
    country: 'Deutschland',
    locale: 'de',
    populationK: 3700,
    topIndustries: ['Behörden + EU-Vertretungen', 'Einzelhandel', 'Start-ups + Tech', 'Tourismus'],
    climateNote: 'Kontinentales Klima — kalte Winter (−15 °C), warme Sommer (+35 °C). IP66 + erweiterter Temperaturbereich Pflicht für Außenkameras.',
    regulations: ['DSGVO + BDSG', 'DIN EN 62676-4', 'VdS 2311', 'TKG (Telemediengesetz)'],
    topLocalBrands: ['bosch', 'hanwha', 'axis', 'dahua'],
    intro: 'Berlin ist der größte CCTV-Markt Deutschlands — Behörden, Bundesministerien, EU-Vertretungen, große Einzelhandelszentren (Alexanderplatz, Ku\'damm) und ein wachsender Tech-Sektor. Projekte unterliegen besonders strenger DSGVO/BDSG-Prüfung durch die Berliner Beauftragte für Datenschutz.',
  },
  {
    slug: 'muenchen',
    name: 'München',
    country: 'Deutschland',
    locale: 'de',
    populationK: 1500,
    topIndustries: ['Automobilindustrie (BMW)', 'Versicherungen (Allianz, Munich Re)', 'Tech (Siemens)', 'Luxus-Einzelhandel'],
    climateNote: 'Alpennähe — starker Schneefall im Winter (−20 °C), Föhn-Wetterumschwünge. Beheizte Gehäuse + IK10 gegen Hagel.',
    regulations: ['DSGVO + BDSG', 'BayDSG (Bayerisches Datenschutzgesetz)', 'DIN EN 62676-4', 'VdS 2311 Klasse C'],
    topLocalBrands: ['bosch', 'axis', 'hanwha', 'mobotix'],
    intro: 'München ist Sitz globaler Konzerne (BMW, Allianz, Siemens, Munich Re) und Premium-Einzelhändler. Sicherheitsprojekte erreichen typische Größen von 50–500 Kameras mit höchsten VdS-Klassifizierungen für Banken und Werks-Sicherheit.',
  },
  {
    slug: 'hamburg',
    name: 'Hamburg',
    country: 'Deutschland',
    locale: 'de',
    populationK: 1900,
    topIndustries: ['Hafen + Logistik (zweitgrößter EU-Hafen)', 'Medien', 'Luftfahrt (Airbus)', 'Banken'],
    climateNote: 'Salzhaltige Hafenluft — korrosionsbeständige Gehäuse (Edelstahl 316). IP67 + Anti-Korrosionsbeschichtung Pflicht.',
    regulations: ['DSGVO + BDSG', 'HmbDSG', 'Hafensicherheitsgesetz', 'EN 50131 Grad 3 (kritische Infrastruktur)'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Hamburgs Hafen verlangt großskalige Perimeter-Sicherheit (Container-Terminals, Schiffsabfertigung). Hamburg-Süd, HHLA und Eurogate sind klassische Auftraggeber. Hinzu kommen Airbus Finkenwerder und Medienkonzerne (Bertelsmann, Spiegel).',
  },
  {
    slug: 'koeln',
    name: 'Köln',
    country: 'Deutschland',
    locale: 'de',
    populationK: 1100,
    topIndustries: ['Versicherungen', 'Medien (RTL, WDR)', 'Messen (Koelnmesse)', 'Logistik'],
    climateNote: 'Rheinisches Klima — milde feuchte Winter, gelegentliche Hochwasser. IP67 + Wasserschutz für tief gelegene Standorte.',
    regulations: ['DSGVO + BDSG', 'LDSG NRW', 'DIN EN 62676-4'],
    topLocalBrands: ['bosch', 'hanwha', 'axis', 'dahua'],
    intro: 'Köln kombiniert Großversicherer (DEVK, Gothaer) mit Mediengiganten (RTL Köln, WDR) und Europas größter Messestadt (Koelnmesse, Photokina). Projekte sind oft Großveranstaltungs-tauglich mit kurzfristiger Skalierung.',
  },
  {
    slug: 'frankfurt',
    name: 'Frankfurt',
    country: 'Deutschland',
    locale: 'de',
    populationK: 770,
    topIndustries: ['Bankenviertel (EZB, Deutsche Bank)', 'Flughafen (zweitgrößter EU)', 'Messen', 'IT-Hubs'],
    climateNote: 'Kontinental, gemäßigt. Standard IP66 ausreichend, Schwerpunkt auf Vandalenschutz im Bahnhofsviertel.',
    regulations: ['DSGVO + BDSG', 'KWG (Kreditwesengesetz)', 'VdS 2311 Klasse C+', 'EN 62676-4 Identify-Level für Bankenfilialen'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Frankfurt ist Deutschlands Finanzhauptstadt — EZB, Deutsche Bank, Commerzbank, sämtliche Großbanken haben Hauptsitze. Sicherheitsprojekte erreichen Banken-Grade-3-Niveau mit dualer Vault-Abdeckung und ATM-Identify-Pixel-Dichte (250 px/m).',
  },
  {
    slug: 'stuttgart',
    name: 'Stuttgart',
    country: 'Deutschland',
    locale: 'de',
    populationK: 630,
    topIndustries: ['Automobilindustrie (Mercedes, Porsche, Bosch)', 'Maschinenbau', 'IT', 'Verlag'],
    climateNote: 'Kessellage, schwüle Sommer, milde Winter. Standard-Schutzklassen reichen aus.',
    regulations: ['DSGVO + BDSG', 'LDSG BW', 'TISAX (Automotive-Lieferantenkette)'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Stuttgart ist Zentrum der deutschen Automobilindustrie. Werks-Sicherheit für Mercedes-Benz, Porsche, Bosch und deren Zulieferer dominiert — TISAX-zertifizierte Anforderungen für Lieferantenstandorte, Werkschutz-Grade-3 für Produktionshallen.',
  },
  {
    slug: 'duesseldorf',
    name: 'Düsseldorf',
    country: 'Deutschland',
    locale: 'de',
    populationK: 620,
    topIndustries: ['Werbung + Mode', 'Telekommunikation (Vodafone)', 'Messen (Boot, drupa)', 'Banken'],
    climateNote: 'Mild, feucht. Standard IP66.',
    regulations: ['DSGVO + BDSG', 'LDSG NRW', 'DIN EN 62676-4'],
    topLocalBrands: ['bosch', 'hanwha', 'axis'],
    intro: 'Düsseldorf ist Mode- und Werbekapitale Deutschlands mit Kö-Bogen, japanischer Diaspora (drittgrößte EU) und Messezentrum. Projekte mischen Premium-Einzelhandel mit B2B-Konzernzentralen (Henkel, E.ON).',
  },
  {
    slug: 'leipzig',
    name: 'Leipzig',
    country: 'Deutschland',
    locale: 'de',
    populationK: 600,
    topIndustries: ['Logistik (DHL Hub)', 'Automobilindustrie (Porsche, BMW)', 'Messen', 'Universität'],
    climateNote: 'Kontinental, trocken, sehr kalt im Winter (−15 °C). Erweiterter Temperaturbereich für Logistikhallen.',
    regulations: ['DSGVO + BDSG', 'SächsDSDG', 'LuftSiG (DHL Hub-Sicherheit)'],
    topLocalBrands: ['bosch', 'hanwha', 'dahua'],
    intro: 'Leipzig ist das Logistikherz Ostdeutschlands (DHL European Hub, Amazon FC) plus BMW + Porsche Werke. Projekte fokussieren auf großflächige Außen-Perimeter mit FAA-/LuftSiG-Konformität für Cargo-Bereiche.',
  },
  {
    slug: 'dortmund',
    name: 'Dortmund',
    country: 'Deutschland',
    locale: 'de',
    populationK: 590,
    topIndustries: ['Logistik (Westfalenhütte)', 'IT-Standorte', 'Stadion (BVB)', 'Universität'],
    climateNote: 'Mild, feucht. Standard IP66.',
    regulations: ['DSGVO + BDSG', 'LDSG NRW', 'Stadionverordnung (Veranstaltungssicherheit)'],
    topLocalBrands: ['bosch', 'hanwha', 'axis', 'dahua'],
    intro: 'Dortmund kombiniert ehemalige Industrieflächen (jetzt Tech-Parks: Phoenix West, Technologiezentrum) mit massiver Veranstaltungssicherheit für Signal-Iduna-Park (80 000 Plätze). Logistikzentren der Westfalenhütte runden den Markt ab.',
  },
  {
    slug: 'essen',
    name: 'Essen',
    country: 'Deutschland',
    locale: 'de',
    populationK: 580,
    topIndustries: ['Energiekonzerne (RWE, E.ON, Eon)', 'Messen', 'Krankenhäuser', 'Einzelhandel'],
    climateNote: 'Mild, feucht. Standard IP66.',
    regulations: ['DSGVO + BDSG', 'LDSG NRW', 'KRITIS (Energieinfrastruktur)', 'DIN EN 62676-4'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Essen ist Energiehauptstadt Deutschlands (RWE, E.ON Konzernzentralen) — entsprechend gilt KRITIS-Klassifizierung für viele Standorte mit BSI-IT-Grundschutz-Anforderungen. Daneben Universitätsklinikum (1700 Betten) und Messe Essen (Equitana, SPIEL).',
  },
];

// ── Italian cities (Sprint D — 6 cities, IT locale) ───────────────────
// Slug pattern: /it/progettazione-cctv-{slug}
export const IT_CITIES: CityFacts[] = [
  {
    slug: 'roma',
    name: 'Roma',
    country: 'Italia',
    locale: 'it',
    populationK: 2800,
    topIndustries: ['Pubblica amministrazione', 'Turismo + beni culturali', 'Banche', 'Università'],
    climateNote: 'Clima mediterraneo — estati molto calde (+40 °C), inverni miti. Standard IP66, attenzione al sole intenso (WDR + raffreddamento attivo per moduli AI).',
    regulations: ['GDPR + Codice Privacy', 'Provvedimenti Garante per la Protezione dei Dati Personali', 'EN 50131', 'Provvedimento del 8 aprile 2010 (videosorveglianza)'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'bosch'],
    intro: 'Roma combina sede istituzionale (Parlamento, ministeri, ambasciate) con turismo di massa (Colosseo, Vaticano) e ricco patrimonio storico. I progetti CCTV richiedono autorizzazioni speciali da Soprintendenza per istallazioni in zone tutelate UNESCO.',
  },
  {
    slug: 'milano',
    name: 'Milano',
    country: 'Italia',
    locale: 'it',
    populationK: 1400,
    topIndustries: ['Moda + design (Quadrilatero)', 'Banche', 'Fiere (Rho Fiera)', 'Università'],
    climateNote: 'Continentale umido — nebbia in autunno (visibilità ridotta), Smart IR essenziale. Inverno freddo, estati afose.',
    regulations: ['GDPR + Codice Privacy', 'Provvedimenti Garante', 'EN 50131 Grado 3 (banche)', 'EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'bosch', 'hanwha'],
    intro: 'Milano è capitale finanziaria + moda + design d\'Italia. Boutique di lusso del Quadrilatero, sedi bancarie (UniCredit, Intesa Sanpaolo, Mediolanum), torri di Porta Nuova: progetti tipicamente da 30 a 500 telecamere con livello Identify per ingressi e vault.',
  },
  {
    slug: 'napoli',
    name: 'Napoli',
    country: 'Italia',
    locale: 'it',
    populationK: 950,
    topIndustries: ['Porto (terzo Italia)', 'Turismo', 'Manifatturiero', 'Sanità'],
    climateNote: 'Mediterraneo costiero — aria salmastra, custodie in acciaio inox 316 obbligatorie sulle banchine portuali.',
    regulations: ['GDPR + Codice Privacy', 'Codice della Navigazione (sicurezza portuale)', 'EN 50131', 'Provvedimenti Garante'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Napoli ha il terzo porto italiano per movimentazione container, infrastruttura turistica intensa (centro storico UNESCO) e crescente settore healthcare (Federico II). Le installazioni perimetrali portuali dominano i grandi progetti.',
  },
  {
    slug: 'torino',
    name: 'Torino',
    country: 'Italia',
    locale: 'it',
    populationK: 870,
    topIndustries: ['Automotive (Stellantis)', 'Aerospaziale', 'Università', 'Eventi (Salone del Libro)'],
    climateNote: 'Continentale, inverni freddi (−10 °C). IP66 con riscaldamento per esterni alpini.',
    regulations: ['GDPR + Codice Privacy', 'EN 50131', 'TISAX (filiera automotive)'],
    topLocalBrands: ['hikvision', 'bosch', 'hanwha', 'axis'],
    intro: 'Torino è cuore automobilistico italiano (Stellantis ex FCA, Iveco) con cintura di fornitori automotive che richiedono certificazione TISAX. Politecnico di Torino e Aerospaziale Leonardo aggiungono complessi tech a sicurezza elevata.',
  },
  {
    slug: 'bologna',
    name: 'Bologna',
    country: 'Italia',
    locale: 'it',
    populationK: 390,
    topIndustries: ['Fiere (Cosmoprof, SAIE)', 'Università più antica EU', 'Logistica (Interporto)', 'Automotive (Ducati, Lamborghini, Ferrari nearby)'],
    climateNote: 'Pianura padana — afa estiva (+40 °C) e nebbia invernale. WDR + Smart IR sempre raccomandati.',
    regulations: ['GDPR + Codice Privacy', 'EN 50131', 'TISAX (fornitori automotive)'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Bologna ospita la fiera Cosmoprof (cosmetica mondiale), l\'università più antica d\'Europa, e l\'Interporto (uno dei più grandi snodi logistici EU). Distretto motoristico circostante (Ducati, Lamborghini Sant\'Agata, Ferrari Maranello) genera richiesta TISAX.',
  },
  {
    slug: 'firenze',
    name: 'Firenze',
    country: 'Italia',
    locale: 'it',
    populationK: 380,
    topIndustries: ['Turismo (centro UNESCO)', 'Moda (Pitti Uomo)', 'Università', 'Banche'],
    climateNote: 'Mediterraneo collinare — estati molto calde, inverni miti. Standard IP66, soluzioni discrete per zone storiche.',
    regulations: ['GDPR + Codice Privacy', 'Soprintendenza per i Beni Culturali (zone UNESCO)', 'EN 50131', 'Provvedimenti Garante'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Firenze è cuore turistico toscano con centro storico patrimonio UNESCO — restrizioni rigorose della Soprintendenza per qualsiasi installazione visibile. Pitti Uomo (moda) e settore bancario (UniCredit, BNL filiali storiche) richiedono integrazione architettonica con compliance Identify-level.',
  },
];

// ── Spanish cities (Sprint D — 6 cities, ES locale) ───────────────────
// Slug pattern: /es/diseno-cctv-{slug}
export const ES_CITIES: CityFacts[] = [
  {
    slug: 'madrid',
    name: 'Madrid',
    country: 'España',
    locale: 'es',
    populationK: 3300,
    topIndustries: ['Administración pública', 'Banca (Santander, BBVA)', 'Telecom (Telefónica)', 'Hostelería'],
    climateNote: 'Clima continental — veranos muy cálidos (+40 °C), inviernos fríos (−5 °C). WDR + IP66 + tratamiento UV para zonas expuestas.',
    regulations: ['RGPD + LOPDGDD', 'Instrucción 1/2006 de la AEPD (videovigilancia)', 'UNE-EN 62676-4', 'Ley 5/2014 de Seguridad Privada'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'bosch'],
    intro: 'Madrid es capital institucional y financiera de España. Sedes de Santander, BBVA, Telefónica, y administración central (Moncloa, ministerios) requieren cumplimiento AEPD estricto. Sector hostelería (hoteles 5 estrellas) y centros comerciales (Gran Vía, Salamanca) son segmentos consolidados.',
  },
  {
    slug: 'barcelona',
    name: 'Barcelona',
    country: 'España',
    locale: 'es',
    populationK: 1600,
    topIndustries: ['Turismo + cruceros', 'Tech (Mobile World Congress)', 'Logística portuaria', 'Comercio'],
    climateNote: 'Mediterráneo costero — aire salino exige carcasas acero 316. Veranos cálidos y húmedos.',
    regulations: ['RGPD + LOPDGDD', 'Llei 13/1989 (Catalunya, datos)', 'Autoritat Catalana de Protecció de Dades (APDCAT)', 'UNE-EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'axis'],
    intro: 'Barcelona combina turismo masivo (Sagrada Familia, La Rambla, cruceros), tech (Mobile World Congress, 22@), y puerto Mediterráneo. APDCAT es regulador autonómico estricto. Proyectos hoteleros y de centros comerciales (Diagonal Mar, La Maquinista) son volúmenes habituales.',
  },
  {
    slug: 'valencia',
    name: 'Valencia',
    country: 'España',
    locale: 'es',
    populationK: 800,
    topIndustries: ['Puerto (quinto Mediterráneo)', 'Turismo', 'Logística + cítricos', 'Tech (Ciudad de las Artes)'],
    climateNote: 'Mediterráneo — veranos calurosos, inviernos suaves. IP66 + tratamiento corrosión para zonas portuarias.',
    regulations: ['RGPD + LOPDGDD', 'AEPD', 'Ley de Seguridad Marítima', 'UNE-EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Valencia es el quinto puerto del Mediterráneo, hub logístico cítrico y turismo emergente (Ciudad de las Artes y las Ciencias). Proyectos típicos mezclan seguridad portuaria perimetral con instalaciones turísticas en zonas históricas (Lonja, Catedral).',
  },
  {
    slug: 'sevilla',
    name: 'Sevilla',
    country: 'España',
    locale: 'es',
    populationK: 690,
    topIndustries: ['Turismo (Semana Santa, Feria)', 'Aeronáutica (Airbus)', 'Administración Junta de Andalucía', 'Logística'],
    climateNote: 'Continental cálido — la ciudad más calurosa de Europa en verano (+45 °C). Sistemas de refrigeración activa para módulos AI imprescindibles.',
    regulations: ['RGPD + LOPDGDD', 'AEPD', 'Ley 4/2017 Andalucía (datos)', 'UNE-EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Sevilla concentra turismo cultural (Catedral, Alcázar UNESCO), industria aeronáutica (Airbus Defence and Space en San Pablo) y administración regional. Las altas temperaturas y polvo del Aljarafe condicionan selección de cámaras y mantenimiento.',
  },
  {
    slug: 'bilbao',
    name: 'Bilbao',
    country: 'España',
    locale: 'es',
    populationK: 350,
    topIndustries: ['Banca (BBVA, Kutxabank)', 'Industria pesada + acero', 'Puerto', 'Cultura (Guggenheim)'],
    climateNote: 'Atlántico — alta humedad, lluvia frecuente. IP67 + tratamiento corrosión obligatorio.',
    regulations: ['RGPD + LOPDGDD', 'AEPD', 'Agencia Vasca de Protección de Datos (AVPD)', 'UNE-EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'axis', 'hanwha'],
    intro: 'Bilbao es centro financiero vasco (sede BBVA) e industrial (siderurgia, astilleros). AVPD es regulador autonómico estricto. Museo Guggenheim, palacios de congresos y la Ría requieren proyectos arquitectónicamente integrados.',
  },
  {
    slug: 'zaragoza',
    name: 'Zaragoza',
    country: 'España',
    locale: 'es',
    populationK: 670,
    topIndustries: ['Logística (PLAZA hub)', 'Automoción (Stellantis Figueruelas)', 'Universidad', 'Aragonia retail'],
    climateNote: 'Continental seco — cierzo (viento fuerte) extra carga estructural en mástiles. Inviernos fríos, veranos calurosos.',
    regulations: ['RGPD + LOPDGDD', 'AEPD', 'TISAX (filial automoción)', 'UNE-EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Zaragoza alberga PLAZA — la mayor plataforma logística del sur de Europa — y planta Stellantis Figueruelas (Opel histórica) que arrastra red de proveedores con certificación TISAX. Universidad de Zaragoza añade campus extenso.',
  },
];

// ── Czech cities (Sprint C — 5 cities, CS locale) ─────────────────────
// Slug pattern: /cs/navrh-cctv-{slug}
export const CS_CITIES: CityFacts[] = [
  {
    slug: 'praha',
    name: 'Praha',
    country: 'Česká republika',
    locale: 'cs',
    populationK: 1300,
    topIndustries: ['Turismus + UNESCO památky', 'Banky (Česká spořitelna)', 'IT + start-upy', 'Administrativa'],
    climateNote: 'Mírné kontinentální klima — chladné zimy (−15 °C), teplá léta. Smog v zimě snižuje viditelnost, doporučujeme Smart IR.',
    regulations: ['GDPR + zákon č. 110/2019 Sb.', 'ÚOOÚ (Úřad pro ochranu osobních údajů)', 'ČSN EN 62676-4', 'Zákon o ochraně osob a majetku'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'bosch'],
    intro: 'Praha je největším českým trhem bezpečnostních systémů — sídla bank, ministerstev, historické centrum UNESCO a masivní turistický ruch. Projekty v památkové zóně vyžadují souhlas Národního památkového ústavu pro viditelné instalace.',
    inflect: { locative: 'Praze', genitive: 'Prahy' },
},
  {
    slug: 'brno',
    name: 'Brno',
    country: 'Česká republika',
    locale: 'cs',
    populationK: 380,
    topIndustries: ['IT (Red Hat, AVG, Honeywell)', 'Veletrhy (BVV)', 'Univerzity', 'Soudnictví (Ústavní soud)'],
    climateNote: 'Mírné kontinentální klima. Standardní IP66 stačí.',
    regulations: ['GDPR + zákon č. 110/2019 Sb.', 'ÚOOÚ', 'ČSN EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Brno je druhým největším českým městem a centrem IT (Red Hat European HQ, AVG, Honeywell). Veletrhy Brno (BVV) jsou největším výstavištěm v Česku — projekty kombinují kancelářské tower s rozsáhlým venkovním perimetrem.',
    inflect: { locative: 'Brně', genitive: 'Brna' },
},
  {
    slug: 'ostrava',
    name: 'Ostrava',
    country: 'Česká republika',
    locale: 'cs',
    populationK: 290,
    topIndustries: ['Těžký průmysl (ArcelorMittal)', 'Logistika', 'Hokej (HC Vítkovice)', 'Univerzity'],
    climateNote: 'Průmyslový smog stále aktuální (i přes pokles uhelné těžby). Smart IR + pravidelný úklid optiky.',
    regulations: ['GDPR + zákon č. 110/2019 Sb.', 'ÚOOÚ', 'ČSN EN 62676-4', 'Báňský zákon (důlní operace)'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Ostrava prochází transformací z těžkého průmyslu (ArcelorMittal stále aktivní) směrem k logistice a IT (Dolní Vítkovice). Projekty často kombinují průmyslový perimetr s občanskou infrastrukturou.',
    inflect: { locative: 'Ostravě', genitive: 'Ostravy' },
},
  {
    slug: 'plzen',
    name: 'Plzeň',
    country: 'Česká republika',
    locale: 'cs',
    populationK: 170,
    topIndustries: ['Strojírenství (Škoda Transportation, Doosan)', 'Pivovary (Plzeňský Prazdroj)', 'Univerzita', 'IT'],
    climateNote: 'Mírné kontinentální klima. Standardní IP66.',
    regulations: ['GDPR + zákon č. 110/2019 Sb.', 'ÚOOÚ', 'ČSN EN 62676-4', 'TISAX (automotive dodavatelé)'],
    topLocalBrands: ['hikvision', 'dahua', 'bosch', 'hanwha'],
    intro: 'Plzeň je centrem českého strojírenství (Škoda Transportation = vlaky/tramvaje, Doosan Škoda Power = turbíny). Plzeňský Prazdroj (Asahi Group) je největší českou pivovarskou skupinou — projekty zahrnují HACCP-konformní instalace ve výrobních halách.',
    inflect: { locative: 'Plzni', genitive: 'Plzně' },
},
  {
    slug: 'olomouc',
    name: 'Olomouc',
    country: 'Česká republika',
    locale: 'cs',
    populationK: 100,
    topIndustries: ['Univerzita (Univerzita Palackého)', 'Potravinářství (Hanácká kyselka)', 'Logistika', 'Zdravotnictví'],
    climateNote: 'Mírné kontinentální klima. Standardní IP66.',
    regulations: ['GDPR + zákon č. 110/2019 Sb.', 'ÚOOÚ', 'ČSN EN 62676-4'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha'],
    intro: 'Olomouc je univerzitním a logistickým centrem střední Moravy. Univerzita Palackého (druhá nejstarší v Česku) a Fakultní nemocnice jsou hlavními zákazníky pro velkoplošné CCTV projekty s GDPR compliance pro zdravotnictví.',
    inflect: { locative: 'Olomouci', genitive: 'Olomouce' },
},
];

// ── Portuguese-Brazilian cities (Sprint C — 5 cities, pt-br locale) ──
// Slug pattern: /pt-br/projeto-cftv-{slug}
export const PT_BR_CITIES: CityFacts[] = [
  {
    slug: 'sao-paulo',
    name: 'São Paulo',
    country: 'Brasil',
    locale: 'pt-br',
    populationK: 12500,
    topIndustries: ['Bancos (sede Itaú, Bradesco, Santander)', 'Indústria', 'Varejo (Av. Paulista, Faria Lima)', 'Tech (Vila Olímpia)'],
    climateNote: 'Subtropical úmido — chuvas intensas (alagamentos zona sul), IP67 obrigatório. Verão quente, inverno ameno.',
    regulations: ['LGPD (Lei Geral de Proteção de Dados)', 'ANPD (Autoridade Nacional de Proteção de Dados)', 'NBR IEC 62676-4', 'NR-12 (segurança em maquinário)'],
    topLocalBrands: ['intelbras', 'hikvision', 'dahua', 'hanwha'],
    intro: 'São Paulo é o maior mercado de CFTV da América Latina — sede dos principais bancos brasileiros (Itaú, Bradesco, Santander), avenida Paulista e Faria Lima (financeiro) e indústrias automotivas no ABC. Projetos chegam a 500+ câmeras com nível Identify para portarias e cofres.',
  },
  {
    slug: 'rio-de-janeiro',
    name: 'Rio de Janeiro',
    country: 'Brasil',
    locale: 'pt-br',
    populationK: 6700,
    topIndustries: ['Petróleo (Petrobras)', 'Turismo (Copacabana, Cristo)', 'Hotelaria', 'Comércio'],
    climateNote: 'Tropical úmido — maresia exige aço inox 316. Calor extremo + UV. Chuvas concentradas no verão.',
    regulations: ['LGPD', 'ANPD', 'NBR IEC 62676-4', 'Resoluções ANP (petróleo)', 'Lei Estadual 4.435/2004 (vigilância privada)'],
    topLocalBrands: ['intelbras', 'hikvision', 'dahua', 'hanwha'],
    intro: 'Rio combina turismo internacional (Copacabana, Pão de Açúcar, Cristo Redentor) com sede da Petrobras e refinarias. Hotéis 5 estrelas e shopping centers (Barra Shopping, Village Mall) demandam grandes projetos. Comunidades pacificadas adicionam complexidade.',
  },
  {
    slug: 'belo-horizonte',
    name: 'Belo Horizonte',
    country: 'Brasil',
    locale: 'pt-br',
    populationK: 2500,
    topIndustries: ['Mineração (Vale)', 'Indústria (FIAT Betim)', 'Saúde', 'Tech (San Pedro Valley)'],
    climateNote: 'Tropical de altitude — clima ameno, pouca chuva. Standard IP66.',
    regulations: ['LGPD', 'ANPD', 'NBR IEC 62676-4', 'NRM-15 (mineração)', 'TISAX (cadeia automotiva)'],
    topLocalBrands: ['intelbras', 'hikvision', 'dahua', 'hanwha'],
    intro: 'Belo Horizonte é capital de Minas Gerais, sede operacional da Vale (segunda maior mineradora global), FIAT Chrysler Betim (maior planta automotiva fora da Itália) e crescente ecossistema tech (San Pedro Valley). Projetos misturam perímetros industriais com segurança corporativa.',
  },
  {
    slug: 'brasilia',
    name: 'Brasília',
    country: 'Brasil',
    locale: 'pt-br',
    populationK: 3100,
    topIndustries: ['Administração federal (Esplanada, Palácios)', 'Embaixadas', 'Universidades', 'Sede de bancos públicos'],
    climateNote: 'Tropical de altitude seco — período seco rigoroso (poeira). Limpeza frequente da óptica.',
    regulations: ['LGPD', 'ANPD', 'NBR IEC 62676-4', 'Diretrizes do Gabinete de Segurança Institucional (GSI/PR) para áreas oficiais'],
    topLocalBrands: ['axis', 'bosch', 'hanwha', 'intelbras'],
    intro: 'Brasília é sede do governo federal brasileiro — Palácio do Planalto, Congresso, STF, ministérios. Embaixadas concentradas na Asa Sul. Projetos governamentais exigem certificação ICP-Brasil e processos do GSI/PR, com preferência por marcas NDAA-compliant (Axis, Bosch, Hanwha).',
  },
  {
    slug: 'porto-alegre',
    name: 'Porto Alegre',
    country: 'Brasil',
    locale: 'pt-br',
    populationK: 1500,
    topIndustries: ['Agronegócio (sede Brasil)', 'Petroquímica', 'Tech (Tecnopuc)', 'Hospitalar'],
    climateNote: 'Subtropical — invernos frios para padrão brasileiro (5 °C), verões muito quentes. Inundações periódicas do Guaíba — IP68 para áreas ribeirinhas.',
    regulations: ['LGPD', 'ANPD', 'NBR IEC 62676-4', 'Normas Coraza-RS (vigilância privada)'],
    topLocalBrands: ['intelbras', 'hikvision', 'dahua', 'hanwha'],
    intro: 'Porto Alegre é centro do agronegócio gaúcho (Cooperativas, exportadores), petroquímica (Polo do Sul) e tech (Tecnopuc, parque tecnológico da PUCRS). Após enchentes 2024 do Guaíba, projetos privilegiam câmeras IP68 e racks elevados em zonas inundáveis.',
  },
];

// ── Dutch cities (Sprint D — 5 cities, NL locale) ─────────────────────
// Slug pattern: /nl/cctv-ontwerp-{slug}
export const NL_CITIES: CityFacts[] = [
  {
    slug: 'amsterdam',
    name: 'Amsterdam',
    country: 'Nederland',
    locale: 'nl',
    populationK: 870,
    topIndustries: ['Banken (ING)', 'Tech (Adyen, Booking)', 'Toerisme (grachtengordel)', 'Haven'],
    climateNote: 'Atlantisch klimaat — veel regen, milde winters, zoute zeelucht (RVS 316 bij havenlocaties). IP67 standaard.',
    regulations: ['AVG + UAVG', 'Autoriteit Persoonsgegevens (AP)', 'NEN-EN 62676-4', 'Wet politiegegevens'],
    topLocalBrands: ['bosch', 'axis', 'hanwha', 'dahua'],
    intro: 'Amsterdam combineert hoofdkantoren van ING, Adyen en Booking met intensief toerisme (grachtengordel UNESCO) en haven activiteiten. AP is strenge regulator op cameratoezicht in openbare ruimte; vergunningen via gemeente vereist.',
  },
  {
    slug: 'rotterdam',
    name: 'Rotterdam',
    country: 'Nederland',
    locale: 'nl',
    populationK: 660,
    topIndustries: ['Haven (grootste EU)', 'Petrochemie', 'Logistiek', 'Architectuur (skyline)'],
    climateNote: 'Atlantisch — sterke wind langs haven, zout aerosol. RVS 316 + IP67 + extra storm-gemonteerde mast versterking.',
    regulations: ['AVG + UAVG', 'AP', 'NEN-EN 62676-4', 'Havenverordening Rotterdam', 'Brzo (Seveso III) voor petrochemie'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Rotterdam heeft de grootste haven van Europa — container terminals, raffinaderijen (Shell Pernis), petrochemische clusters. Projecten zijn typisch perimeter-veiligheid op industriële schaal met Brzo-richtlijn voor opslagtanks.',
  },
  {
    slug: 'den-haag',
    name: 'Den Haag',
    country: 'Nederland',
    locale: 'nl',
    populationK: 550,
    topIndustries: ['Regering + ministeries', 'Internationaal gerechtshof', 'Ambassades', 'Toerisme (Scheveningen)'],
    climateNote: 'Kustklimaat — wind en zoute lucht, IP67 + RVS housings nabij Scheveningen.',
    regulations: ['AVG + UAVG', 'AP', 'NEN-EN 62676-4', 'Specifieke richtlijnen NCTV voor regeringsgebouwen'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Den Haag is regeringscentrum (Binnenhof, ministeries) en zetel van het Internationaal Gerechtshof. Projecten vereisen NCTV-richtlijnen en hoge VdS-equivalente certificering. NDAA-compliant merken (Axis, Bosch, Hanwha) hebben sterke voorkeur voor overheid.',
  },
  {
    slug: 'utrecht',
    name: 'Utrecht',
    country: 'Nederland',
    locale: 'nl',
    populationK: 360,
    topIndustries: ['Universiteit', 'Financiën (Rabobank)', 'Spoorwegcentrum (NS HQ)', 'Conferenties (Jaarbeurs)'],
    climateNote: 'Continentaal-atlantisch. Standaard IP66.',
    regulations: ['AVG + UAVG', 'AP', 'NEN-EN 62676-4'],
    topLocalBrands: ['bosch', 'axis', 'hanwha'],
    intro: 'Utrecht is geografisch en spoorwegcentrum van Nederland (NS HQ, Centraal Station = grootste van Nederland). Rabobank hoofdkantoor en Universiteit Utrecht (oudste van Nederland) drijven veel CCTV projecten.',
  },
  {
    slug: 'eindhoven',
    name: 'Eindhoven',
    country: 'Nederland',
    locale: 'nl',
    populationK: 240,
    topIndustries: ['Tech (Brainport, ASML, Philips)', 'High-tech industrie', 'Universiteit (TU/e)', 'Logistiek'],
    climateNote: 'Continentaal. Standaard IP66.',
    regulations: ['AVG + UAVG', 'AP', 'NEN-EN 62676-4', 'TISAX (high-tech leveranciers)'],
    topLocalBrands: ['bosch', 'axis', 'hanwha', 'mobotix'],
    intro: 'Eindhoven is het Brainport-gebied — ASML (lithography machines voor halfgeleiders), Philips, TU/e. Project security richt zich op IP-bescherming voor high-tech R&D faciliteiten met TISAX-niveau toegangscontrole en streng cameratoezicht in cleanrooms.',
  },
];

// Extend EN_CITIES with Australia + South Africa (Sprints B + C tail)
// These countries use English natively so no separate locale; the country
// field differentiates them from US/UK rows.
export const AU_CITIES: CityFacts[] = [
  {
    slug: 'sydney',
    name: 'Sydney',
    country: 'Australia',
    locale: 'en',
    populationK: 5400,
    topIndustries: ['Finance (Westpac, CBA, Macquarie)', 'Tech (Atlassian)', 'Tourism (Opera House)', 'Port (Botany)'],
    climateNote: 'Coastal subtropical — humid summers, marine corrosion near harbour. 316 stainless housings + IP67 mandatory for portside.',
    regulations: ['Privacy Act 1988 (Cth)', 'AS 4806 (Closed Circuit Television)', 'NSW Workplace Surveillance Act 2005', 'CASA regulations (airport)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha', 'hikvision'],
    intro: 'Sydney is Australia\'s largest CCTV market — major bank HQs (Westpac, Commonwealth Bank, Macquarie), tech (Atlassian), and high-tourism precincts (Opera House, Bondi). NSW Workplace Surveillance Act adds employer-specific notification requirements on top of federal Privacy Act 1988.',
  },
  {
    slug: 'melbourne',
    name: 'Melbourne',
    country: 'Australia',
    locale: 'en',
    populationK: 5100,
    topIndustries: ['Finance (NAB, ANZ)', 'Universities', 'Sporting venues (MCG, AAMI Park)', 'Manufacturing'],
    climateNote: '"Four seasons in one day" — wide diurnal temperature swing, sudden squalls. IP66 + heated housings for southern winter.',
    regulations: ['Privacy Act 1988 (Cth)', 'AS 4806', 'Victorian Privacy and Data Protection Act 2014', 'CASA (airport)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha', 'hikvision'],
    intro: 'Melbourne hosts NAB + ANZ Bank HQs, major universities (Melbourne, Monash, RMIT) and high-capacity sporting venues (MCG = 100k seats). Victoria has separate state Privacy Act adding patient/student-specific requirements beyond federal law.',
  },
  {
    slug: 'brisbane',
    name: 'Brisbane',
    country: 'Australia',
    locale: 'en',
    populationK: 2700,
    topIndustries: ['Mining HQs (BHP, Rio Tinto offices)', 'Port logistics', 'Tourism gateway (Great Barrier Reef)', 'Olympics 2032 prep'],
    climateNote: 'Subtropical — hot humid summers, occasional severe storms (hail). IP67 + IK10 + UV resistant housings.',
    regulations: ['Privacy Act 1988 (Cth)', 'AS 4806', 'Queensland Information Privacy Act 2009', 'Maritime Transport Security Act'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Brisbane is Queensland\'s capital — mining majors\' admin offices, the Port of Brisbane, and 2032 Olympics infrastructure ramp-up underway. Subtropical storms drive demand for IK10 + UV-resistant equipment.',
  },
  {
    slug: 'perth',
    name: 'Perth',
    country: 'Australia',
    locale: 'en',
    populationK: 2200,
    topIndustries: ['Mining (BHP, Rio Tinto operational HQs)', 'Oil & gas', 'Defence (HMAS Stirling)', 'Universities'],
    climateNote: 'Mediterranean — long hot dry summers (+40 °C), mild wet winters. Dust loading + active cooling for AI modules.',
    regulations: ['Privacy Act 1988 (Cth)', 'AS 4806', 'WA Information Privacy Bill', 'Defence Industry Security Program (DISP)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Perth is the operational headquarters of Australia\'s mining giants (BHP, Rio Tinto, Fortescue) and home to HMAS Stirling naval base. DISP clearance is common requirement for defence-adjacent projects. NDAA-compliant brands (Axis, Bosch, Hanwha) dominate.',
  },
  {
    slug: 'adelaide',
    name: 'Adelaide',
    country: 'Australia',
    locale: 'en',
    populationK: 1400,
    topIndustries: ['Defence (Naval Group, ASC)', 'Wine industry', 'Universities', 'Health/biotech'],
    climateNote: 'Mediterranean — hot dry summers, mild winters. Standard IP66 sufficient.',
    regulations: ['Privacy Act 1988 (Cth)', 'AS 4806', 'SA Information Privacy Principles', 'DISP (defence)'],
    topLocalBrands: ['axis', 'bosch', 'hanwha'],
    intro: 'Adelaide is the centre of Australia\'s defence industry — ASC Shipbuilding (submarines), Naval Group (Attack-class), Lockheed Martin Australia. Wine industry (Barossa, Adelaide Hills) and Universities (Adelaide, Flinders) complete the corporate mix. Heavy DISP requirements.',
  },
];

export const ZA_CITIES: CityFacts[] = [
  {
    slug: 'johannesburg',
    name: 'Johannesburg',
    country: 'South Africa',
    locale: 'en',
    populationK: 6000,
    topIndustries: ['Banking (Standard Bank, FirstRand, Absa)', 'Mining HQs', 'Sandton CBD', 'Logistics (OR Tambo)'],
    climateNote: 'Highveld — hot dry days, sudden thunderstorms with hail. IK10 + IP66 essential. High UV.',
    regulations: ['POPIA (Protection of Personal Information Act 2013)', 'SAPS PSIRA regulations', 'SANS 60839 (security alarm systems)', 'BCEA (workplace surveillance)'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'axis'],
    intro: 'Johannesburg is Africa\'s largest CCTV market — Sandton CBD hosts the JSE plus major bank HQs (Standard Bank, FirstRand, Absa, Investec). Mining majors operate from Marshalltown. Crime rate drives heavy commercial + residential CCTV adoption.',
  },
  {
    slug: 'cape-town',
    name: 'Cape Town',
    country: 'South Africa',
    locale: 'en',
    populationK: 4700,
    topIndustries: ['Tourism (V&A Waterfront)', 'Wine industry', 'Tech (Silicon Cape)', 'Port + container terminal'],
    climateNote: 'Mediterranean — strong southeasterly wind ("Cape Doctor"), salt aerosol near coast. RVS 316 + IP67 + reinforced mounts for cable car / Table Mountain installations.',
    regulations: ['POPIA', 'PSIRA', 'SANS 60839', 'WC Provincial regulations'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'axis'],
    intro: 'Cape Town blends tourism (V&A Waterfront, Table Mountain), wine industry (Stellenbosch, Constantia) and tech (Silicon Cape — Naspers ecosystem). Coastal port and cruise terminal add maritime perimeter requirements.',
  },
  {
    slug: 'durban',
    name: 'Durban',
    country: 'South Africa',
    locale: 'en',
    populationK: 3700,
    topIndustries: ['Port (Africa\'s busiest)', 'Manufacturing (Toyota SA)', 'Tourism', 'Sugar industry'],
    climateNote: 'Subtropical coastal — hot humid summers, salt corrosion, cyclonic storms. RVS 316 + IP68 obligatory along beachfront and port.',
    regulations: ['POPIA', 'PSIRA', 'SANS 60839', 'Port of Durban security regs', 'KZN Provincial regulations'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'axis'],
    intro: 'Durban hosts Africa\'s busiest port (Port of Durban, container hub for sub-Saharan trade), Toyota South Africa\'s only assembly plant, and tourism along the Golden Mile. Severe humidity and salt require highest-spec marine housings.',
  },
];

// Merge AU + ZA into the EN array via spread — keeps the existing
// /cctv-design-{slug} route pattern working for them.
export const EN_CITIES_EXTENDED: CityFacts[] = [...EN_CITIES, ...AU_CITIES, ...ZA_CITIES];

// ── Hungarian cities (HU locale) ──────────────────────────────────────
export const HU_CITIES: CityFacts[] = [
  {
    slug: 'budapest',
    name: 'Budapest',
    country: 'Magyarország',
    locale: 'hu',
    populationK: 1700,
    topIndustries: ['Vállalati irodák', 'Kiskereskedelem és bevásárlóközpontok', 'Bankok', 'Turizmus'],
    climateNote: 'Kontinentális éghajlat, télen erős fagyokkal (−15 °C) és nyári hőhullámokkal (+35 °C) — szélsőséges hőmérsékletnek ellenálló kamerák és WDR a téli reflexek ellen szükséges.',
    regulations: ['GDPR', 'Infotörvény (2011. évi CXII. tv.)', 'EN 50131 Grade 2+', 'NAIH irányelvek'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'bosch'],
    intro: 'Budapest Magyarország legnagyobb biztonsági piaca — vállalati központok, bevásárlóközpontok, bankok és lakóparkok koncentrációja. A NAIH iránymutatásai megkövetelik a felvételek megőrzési idejének és láthatósági táblák jelzéseinek pontos kezelését.',
    inflect: { locative: 'Budapesten', genitive: 'Budapest' },
  },
  {
    slug: 'debrecen',
    name: 'Debrecen',
    country: 'Magyarország',
    locale: 'hu',
    populationK: 200,
    topIndustries: ['Egyetemi kampuszok', 'Egészségügy', 'BMW gyár', 'Logisztika'],
    climateNote: 'Hideg telek és meleg nyarak; a porhajtás (Hortobágy közelsége) megköveteli az IP66 minősítésű burkolatokat.',
    regulations: ['GDPR', 'Infotörvény', 'EN 50131 Grade 2', 'Egészségügyi adatvédelem'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview'],
    intro: 'Debrecen kelet-magyarországi ipari és oktatási központ — a BMW új gyára, a Debreceni Egyetem klinikai központja és az Észak-alföldi logisztikai csomópont mind igényes CCTV-projektekkel rendelkezik. A regionális szabványok szigorúan követik a budapesti gyakorlatot.',
    inflect: { locative: 'Debrecenben', genitive: 'Debrecen' },
  },
  {
    slug: 'szeged',
    name: 'Szeged',
    country: 'Magyarország',
    locale: 'hu',
    populationK: 160,
    topIndustries: ['Egyetemi kampusz', 'Szerb határátkelő', 'Élelmiszeripar', 'Mezőgazdaság'],
    climateNote: 'A legtöbb napsütéses óra Magyarországon — kontrasztos jelenetek WDR-t igényelnek. Téli árvízkockázat a Tisza miatt befolyásolja a kültéri kamerák magasságát.',
    regulations: ['GDPR', 'Infotörvény', 'Schengeni határvédelmi előírások'],
    topLocalBrands: ['hikvision', 'dahua', 'axis'],
    intro: 'Szeged a Szegedi Tudományegyetem és a Röszke–Horgos határátkelő miatt különleges biztonsági követelményekkel néz szembe. A határátkelő LPR-rendszerei az országos átlagnál magasabb felbontást és éjszakai teljesítményt igényelnek.',
    inflect: { locative: 'Szegeden', genitive: 'Szeged' },
  },
];

// ── Lithuanian cities (LT locale) ─────────────────────────────────────
export const LT_CITIES: CityFacts[] = [
  {
    slug: 'vilnius',
    name: 'Vilnius',
    country: 'Lietuva',
    locale: 'lt',
    populationK: 580,
    topIndustries: ['Verslo centrai', 'Fintech ir IT', 'Valstybės įstaigos', 'Senamiestis (UNESCO)'],
    climateNote: 'Šaltos žiemos (−25 °C) ir vidutiniškai šiltos vasaros; sniego atspindys reikalauja WDR, o gilus šaltis — pramoninio reitingo kamerų.',
    regulations: ['GDPR / BDAR', 'Lietuvos Respublikos asmens duomenų teisinės apsaugos įstatymas', 'EN 50131 Grade 2+', 'VDAI rekomendacijos'],
    topLocalBrands: ['hikvision', 'dahua', 'hanwha', 'axis'],
    intro: 'Vilnius — Lietuvos finansų ir technologijų sostinė, koncentruojanti tarptautinių bankų atstovybes, fintech įmones (Revolut, Vinted) ir valstybės institucijas. Senamiesčio UNESCO statusas reikalauja diskretiškos kamerų integracijos, o NATO infrastruktūros artumas — sustiprintos kibernetinio saugumo atitikties.',
    inflect: { locative: 'Vilniuje', genitive: 'Vilniaus' },
  },
  {
    slug: 'kaunas',
    name: 'Kaunas',
    country: 'Lietuva',
    locale: 'lt',
    populationK: 290,
    topIndustries: ['Pramonė ir gamyba', 'Logistika ir LEZ', 'Universitetai (KTU, VDU)', 'Sandėliai'],
    climateNote: 'Žemyninis klimatas su staigiais temperatūros pokyčiais; pramoninių zonų dulkės reikalauja IP66+ korpusų.',
    regulations: ['GDPR / BDAR', 'Asmens duomenų teisinės apsaugos įstatymas', 'EN 50131 Grade 2'],
    topLocalBrands: ['hikvision', 'dahua', 'uniview', 'hanwha'],
    intro: 'Kaunas — antras pagal dydį Lietuvos miestas ir pagrindinis pramonės centras. Kauno laisvoji ekonominė zona (LEZ) ir tarptautinis oro uostas kuria didelę paklausą logistikos sandėlių CCTV sprendimams. Pasienio vieta su Lenkija per Suvalkų koridorių didina perimetro stebėjimo svarbą.',
    inflect: { locative: 'Kaune', genitive: 'Kauno' },
  },
  {
    slug: 'klaipeda',
    name: 'Klaipėda',
    country: 'Lietuva',
    locale: 'lt',
    populationK: 150,
    topIndustries: ['Jūrų uostas', 'Logistika ir konteinerių terminalas', 'Laivų statyba', 'Turizmas'],
    climateNote: 'Baltijos pajūrio klimatas — drėgmė ir druskos rūkas reikalauja antikoroziniu danga ir IP67 korpusų; rūkai mažina matomumą, todėl IR ir terminis vaizdas tampa kritiniai.',
    regulations: ['GDPR / BDAR', 'Jūrų uostų saugumo direktyvos', 'ISPS Code', 'Muitinės reikalavimai'],
    topLocalBrands: ['hikvision', 'dahua', 'axis', 'flir'],
    intro: 'Klaipėda — vienintelis Lietuvos jūrų uostas ir strategiškai svarbiausia logistikos jungtis su Vakarų Europa. Uosto teritorijos stebėjimas reikalauja ISPS Code atitikties, perimetro kontrolės ilgais nuotoliais ir specializuotų jūrinių kamerų.',
    inflect: { locative: 'Klaipėdoje', genitive: 'Klaipėdos' },
  },
];

export const ALL_CITIES = [
  ...PL_CITIES,
  ...EN_CITIES_EXTENDED,
  ...DE_CITIES,
  ...IT_CITIES,
  ...ES_CITIES,
  ...CS_CITIES,
  ...PT_BR_CITIES,
  ...NL_CITIES,
  ...HU_CITIES,
  ...LT_CITIES,
];
